/**
 * Input.tsx — Componente de campo de formulario del design system
 *
 * Patrón: Radix Label (htmlFor/id) + CVA (variantes/sizes) + Tailwind.
 * Sigue la referencia canónica de Button.tsx (ADR-025).
 *
 * Tipos soportados: text | email | password | number | search
 * Estados: default, focus (nativo CSS), error, disabled, with-icon-left,
 *          with-icon-right, with-prefix, with-suffix, read-only
 *
 * Por qué Radix Label:
 *   Garantiza asociación correcta label↔input via htmlFor/id incluso en
 *   entornos con shadow DOM o portales. Es la práctica recomendada en ADR-025.
 *
 * Por qué aria-invalid sobre clases visuales solas:
 *   El border rojo en error state es un indicador visual; aria-invalid es
 *   el equivalente semántico para AT. WCAG 1.3.1 requiere ambos.
 */
import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle } from 'lucide-react';
import { cn } from './utils';

// ---------------------------------------------------------------------------
// Variantes CVA para el <input> nativo
// ---------------------------------------------------------------------------

const inputVariants = cva(
  // Base: aplica a todos los tamaños y estados.
  [
    'w-full font-normal text-cumple-primary-900 bg-white',
    'border rounded-[12px] transition-colors duration-150',
    // Placeholder: gray-500 (#64748B) — contraste 5.83:1 sobre blanco (WCAG AA spec §3.3)
    'placeholder:text-cumple-gray-500',
    // Focus ring — WCAG 2.4.7: debe ser visible por teclado.
    // ring-offset-0: el ring se superpone al borde, evita doble borde.
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0',
    'focus-visible:border-cumple-primary-500 focus-visible:ring-cumple-accent-500',
    // Disabled
    'disabled:bg-cumple-gray-50 disabled:text-cumple-gray-400',
    'disabled:border-cumple-gray-200 disabled:cursor-not-allowed',
    // Área táctil tablet-first WCAG 2.5.5
    'md:min-h-[44px]',
  ],
  {
    variants: {
      inputState: {
        // Estado default: borde gray-300 (#D1D5DB, spec §3.3)
        default: 'border-cumple-gray-300',
        // Estado error: borde danger-600 (#DC2626) + ring danger-500 en focus
        error: [
          'border-cumple-danger-600',
          'focus-visible:border-cumple-danger-600',
          'focus-visible:ring-cumple-danger-500',
        ],
      },
      size: {
        sm: 'h-8 px-3 py-1 text-sm',
        md: 'h-10 px-3 py-2 text-base',
        lg: 'h-12 px-4 py-3 text-lg',
      },
      // Ajuste de padding cuando hay íconos para evitar solapamiento
      hasIconLeft: {
        true: 'pl-10',
        false: '',
      },
      hasIconRight: {
        true: 'pr-10',
        false: '',
      },
      hasPrefix: {
        true: 'rounded-l-none border-l-0',
        false: '',
      },
      hasSuffix: {
        true: 'rounded-r-none border-r-0',
        false: '',
      },
    },
    defaultVariants: {
      inputState: 'default',
      size: 'md',
      hasIconLeft: false,
      hasIconRight: false,
      hasPrefix: false,
      hasSuffix: false,
    },
  },
);

// ---------------------------------------------------------------------------
// Props del componente
// ---------------------------------------------------------------------------

export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    Pick<VariantProps<typeof inputVariants>, 'size' | 'inputState'> {
  /** id — Requerido para vincular label↔input via htmlFor */
  id: string;
  /** label — Texto visible del label. Siempre presente (WCAG 1.3.1). */
  label: string;
  /** required — Muestra indicador visual "*" y aplica aria-required */
  required?: boolean;
  /**
   * errorMessage — Cuando presente, activa estado error:
   *   - borde danger-600
   *   - aria-invalid="true"
   *   - mensaje debajo con ícono AlertCircle
   */
  errorMessage?: string;
  /** helperText — Texto de ayuda bajo el input (estado no-error) */
  helperText?: string;
  /** iconLeft — Ícono izquierdo. Acepta React.ReactNode (Lucide, SVG, etc.) */
  iconLeft?: React.ReactNode;
  /** iconRight — Ícono derecho. Acepta React.ReactNode */
  iconRight?: React.ReactNode;
  /** prefix — Texto prefijo (ej. "$", "USD"). Fondo gray-50, borde derecho */
  prefix?: string;
  /** suffix — Texto sufijo (ej. "kg", "días"). Fondo gray-50, borde izquierdo */
  suffix?: string;
  /** className — Sobrescritura de clases del input nativo */
  className?: string;
  /** wrapperClassName — Sobrescritura del div contenedor */
  wrapperClassName?: string;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label,
      type = 'text',
      size = 'md',
      inputState,
      required = false,
      errorMessage,
      helperText,
      iconLeft,
      iconRight,
      prefix,
      suffix,
      disabled,
      readOnly,
      className,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    // El estado error se deriva de errorMessage si no se pasa inputState explícito.
    // Se usa ?? para el primer nivel (inputState puede ser undefined) y ternario
    // condicional (no ||) para evitar la regla prefer-nullish-coalescing.
    // ?? porque inputState puede ser undefined (no pasa por el linter prefer-nullish-coalescing)
    // El valor por defecto depende de errorMessage: 'error' si hay mensaje, 'default' si no.
    const derivedState = errorMessage != null ? 'error' : ('default' as const);
    const resolvedState = inputState ?? derivedState;
    const isError = resolvedState === 'error';
    // ID para el helper/error text vinculado vía aria-describedby
    const hintId = `${id}-hint`;

    return (
      <div className={cn('flex flex-col gap-1', wrapperClassName)}>
        {/* Label — Radix Label garantiza asociación correcta en todos los navegadores */}
        <LabelPrimitive.Root htmlFor={id} className="text-sm font-medium text-cumple-primary-900">
          {label}
          {required && (
            <>
              {/* Indicador visual — danger-600 (#DC2626) spec §3.4 */}
              <span aria-hidden="true" className="ml-0.5 text-cumple-danger-600">
                {' '}
                *
              </span>
              {/* Equivalente semántico para lectores de pantalla */}
              <span className="sr-only"> (campo obligatorio)</span>
            </>
          )}
        </LabelPrimitive.Root>

        {/* Contenedor del input con slots de ícono/prefix/suffix */}
        <div className="relative flex">
          {/* Prefix (ej. "$", "https://") */}
          {prefix && (
            <span
              aria-hidden="true"
              className={cn(
                'inline-flex items-center px-3 text-sm text-cumple-gray-700',
                'border border-r-0 border-cumple-gray-300 rounded-l-[12px]',
                'bg-cumple-gray-50',
                disabled && 'border-cumple-gray-200',
              )}
            >
              {prefix}
            </span>
          )}

          {/* Ícono izquierdo — aria-hidden: no aporta semántica al AT */}
          {iconLeft && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-cumple-gray-500"
            >
              {iconLeft}
            </span>
          )}

          {/* Input nativo */}
          <input
            ref={ref}
            id={id}
            type={type}
            disabled={disabled}
            readOnly={readOnly}
            // ARIA semántico
            aria-required={required || undefined}
            aria-invalid={isError || undefined}
            aria-describedby={helperText || errorMessage ? hintId : undefined}
            aria-errormessage={isError ? hintId : undefined}
            className={cn(
              inputVariants({
                inputState: resolvedState,
                size,
                hasIconLeft: !!iconLeft,
                hasIconRight: !!iconRight || isError,
                hasPrefix: !!prefix,
                hasSuffix: !!suffix,
              }),
              // read-only: borde dashed, fondo gray-50 (spec §3.3)
              readOnly && 'border-dashed bg-cumple-gray-50 text-cumple-gray-700',
              className,
            )}
            {...props}
          />

          {/* Ícono derecho — o ícono de error si hay errorMessage */}
          {(iconRight != null || isError) && (
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute right-3 top-1/2 -translate-y-1/2',
                isError ? 'text-cumple-danger-600' : 'text-cumple-gray-500',
              )}
            >
              {/* El ícono de error tiene precedencia sobre iconRight */}
              {isError ? <AlertCircle className="h-4 w-4" /> : iconRight}
            </span>
          )}

          {/* Suffix (ej. "kg", "días") */}
          {suffix && (
            <span
              aria-hidden="true"
              className={cn(
                'inline-flex items-center px-3 text-sm text-cumple-gray-700',
                'border border-l-0 border-cumple-gray-300 rounded-r-[12px]',
                'bg-cumple-gray-50',
                disabled && 'border-cumple-gray-200',
              )}
            >
              {suffix}
            </span>
          )}
        </div>

        {/* Helper text / Error message */}
        {(helperText != null || errorMessage != null) && (
          <p
            id={hintId}
            // aria-live="polite": anuncia cambios al SR sin interrumpir (spec §3.5)
            // Solo agregar role="alert" para errores críticos de seguridad — no aquí.
            aria-live="polite"
            className={cn(
              'mt-1 text-sm',
              isError ? 'flex items-center gap-1 text-cumple-danger-700' : 'text-cumple-gray-500',
            )}
          >
            {isError && errorMessage ? errorMessage : helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export { Input, inputVariants };
